import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  Button
} from '@mui/material';
import {
  Visibility,
  Person,
  CalendarToday,
  SwapHoriz,
  MoveDown,
  MoveUp,
  LocalShipping,
  CompareArrows
} from '@mui/icons-material';
import { DataService } from '../services/data-service';
import { Operation, OperationListResponse, TransferType } from '../types/Operation.types';
import { format } from 'date-fns';

interface OperationsListProps {
  transferType?: TransferType | null; // Optional, null means all operations
  onOperationSelect: (operation: Operation) => void;
  onTotalCountUpdate?: (count: number) => void;
  hideHeader?: boolean;
}

export interface OperationsListRef {
  refresh: () => void;
}

const getTransferTypeInfo = (type: TransferType) => {
  switch (type) {
    case TransferType.MoveIn:
      return { label: 'Move In', color: 'success' as const, icon: <MoveDown /> };
    case TransferType.MoveOut:
      return { label: 'Move Out', color: 'warning' as const, icon: <MoveUp /> };
    case TransferType.Reallocation:
      return { label: 'Reallocation', color: 'info' as const, icon: <SwapHoriz /> };
    case TransferType.Shipment:
      return { label: 'Shipment', color: 'primary' as const, icon: <LocalShipping /> };
    case TransferType.Transfer:
      return { label: 'Transfer', color: 'secondary' as const, icon: <CompareArrows /> };
    default:
      return { label: 'Unknown', color: 'default' as const, icon: <SwapHoriz /> };
  }
};

const OperationsList = forwardRef<OperationsListRef, OperationsListProps>(({ transferType, onOperationSelect, onTotalCountUpdate, hideHeader = false }, ref) => {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  const fetchOperations = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);

    try {
      // Build URL with conditional type parameter
      const url = transferType !== null && transferType !== undefined
        ? `api/Operation?page=${page}&pageSize=${pageSize}&type=${transferType}`
        : `api/Operation?page=${page}&pageSize=${pageSize}`;

      const response = await DataService.get(url);

      if (!response.ok) {
        throw new Error('Failed to fetch operations');
      }

      const data: OperationListResponse = await response.json();

      setOperations(data.items || []);
      const count = data.totalCount || 0;
      setTotalCount(count);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.pageNumber || 1);

      if (onTotalCountUpdate) {
        onTotalCountUpdate(count);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch operations');
      setOperations([]);
    } finally {
      setLoading(false);
    }
  }, [transferType, pageSize, onTotalCountUpdate]);

  // Fetch operations when transferType changes (route change)
  useEffect(() => {
    fetchOperations(1);
  }, [transferType]);

  // Initial load
  useEffect(() => {
    fetchOperations(1);
  }, []);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    fetchOperations(page);
  };

  const handleOperationClick = (operation: Operation) => {
    onOperationSelect(operation);
  };

  useImperativeHandle(ref, () => ({
    refresh: () => fetchOperations(currentPage)
  }));

  const transferTypeInfo = getTransferTypeInfo(transferType);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" action={
          <Button onClick={() => fetchOperations(currentPage)}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      {!hideHeader && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                {transferTypeInfo.icon}
                <Typography variant="h6">
                  {transferTypeInfo.label} Operations
                </Typography>
                <Chip
                  label={`${totalCount} Total`}
                  size="small"
                  color={transferTypeInfo.color}
                  variant="outlined"
                />
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={() => fetchOperations(currentPage)}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : undefined}
              >
                Refresh
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Operations Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 'auto' }}>Type</TableCell>
              <TableCell sx={{ width: 'auto' }}>Operation</TableCell>
              <TableCell sx={{ minWidth: 180, width: 'auto' }}>Date & Time</TableCell>
              <TableCell sx={{ width: 'auto' }}>User</TableCell>
              <TableCell sx={{ width: 'auto' }}>Transfers</TableCell>
              <TableCell sx={{ width: 'auto' }}>Reference</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {operations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No {transferType ? getTransferTypeInfo(transferType).label.toLowerCase() : 'operations'} found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              operations.map((operation) => {
                const operationTypeInfo = getTransferTypeInfo(operation.type);
                return (
                  <TableRow
                    key={operation.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOperationClick(operation)}
                  >
                    <TableCell>
                      <Tooltip title={operationTypeInfo.label}>
                        <Chip
                          icon={operationTypeInfo.icon}
                          label={operationTypeInfo.label}
                          size="small"
                          color={operationTypeInfo.color}
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {operation.name || 'Unnamed Operation'}
                        </Typography>
                        {operation.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {operation.notes}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarToday sx={{ fontSize: 16, color: 'action.active' }} />
                        <Box sx={{ minWidth: 140 }}>
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            {format(new Date(operation.timestamp), 'MMM dd, yyyy')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {format(new Date(operation.timestamp), 'HH:mm:ss')}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Person sx={{ fontSize: 16, color: 'action.active' }} />
                        <Typography variant="body2" noWrap>
                          {operation.userName || 'Unknown'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${operation.transfers.length} transfers`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 120 }}>
                        {operation.refNumber && (
                          <Typography variant="body2" fontWeight="medium" noWrap>
                            {operation.refNumber}
                          </Typography>
                        )}
                        {operation.referenceId && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            Ref: {operation.referenceId}
                          </Typography>
                        )}
                        {!operation.refNumber && !operation.referenceId && (
                          <Typography variant="caption" color="text.secondary">
                            No reference
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
});

OperationsList.displayName = 'OperationsList';

export default OperationsList;